import { forwardRef, useState } from "react";
import { Typography } from "@mui/material";
import {
  ANIMATING_IMAGE_TITLE,
  ANIMATING_IMAGE_SUCCESS,
  ANIMATING_IMAGE_ERROR,
  DISCARD_IMAGE_ANIMATION_ALERT_DESCRIPTION,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { ALERTS_ASYNC_STATUS } from "../../utils/constants";
import { CREATIVE_JOB_TYPES } from "../../utils/errors";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import { openAlertModal } from "../../utils/alertModalsState";
import TaskAlertCard from "./TaskAlertCard";
import TaskAlertErrorMessage from "./TaskAlertErrorMessage";
import TaskAlertDiscardActions from "./TaskAlertDiscardActions";
import { useCreativeJobProgressLoader } from "../../hooks/useCreativeJobProgressLoader";

const getTaskVisualStatus = ({ isError, isSuccess, isLoading }) => {
  if (isError) return "error";
  if (isSuccess) return "success";
  if (isLoading) return "inProgress";
  return "pending";
};

const ImageAnimationAlertItem = forwardRef(({ alert, onClose }, ref) => {
  const { t } = useTranslationApp();
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const { id, status, progress, generationData, message, jobID } = alert;

  const isError = status === ALERTS_ASYNC_STATUS.ERROR;
  const isSuccess = status === ALERTS_ASYNC_STATUS.SUCCESS;
  const isLoading = status === ALERTS_ASYNC_STATUS.LOADING;
  const isTerminal = isError || isSuccess;
  const storageKey = jobID ?? id;

  const {
    loaderTimeMs,
    onProgressChange,
    seedProgress,
    formattedRemainingTime,
  } = useCreativeJobProgressLoader({
    storageKey,
    migrateFromKey: id,
    jobType: CREATIVE_JOB_TYPES.animateImage,
    isTerminal,
  });

  const getTranslatedMessage = () => {
    if (isError) return t(ANIMATING_IMAGE_ERROR);
    if (isSuccess) return t(ANIMATING_IMAGE_SUCCESS);
    return t(ANIMATING_IMAGE_TITLE);
  };

  const onAlertClick = () => {
    if (isConfirmingDiscard) return;

    const modalData = generationData || { modalType: "gallery" };

    openAlertModal(id, modalData, () => {
      if (
        status === ALERTS_ASYNC_STATUS.SUCCESS ||
        status === ALERTS_ASYNC_STATUS.ERROR
      ) {
        onClose(id);
      }
    });
  };

  const taskVisualStatus = getTaskVisualStatus({
    isError,
    isSuccess,
    isLoading,
  });
  const displayProgress = progress || (isLoading ? seedProgress : 100);
  const showDiscardButton = isError || isSuccess;

  return (
    <TaskAlertCard
      ref={ref}
      status={taskVisualStatus}
      label={getTranslatedMessage()}
      estimatedTime={isLoading ? formattedRemainingTime : null}
      progress={displayProgress}
      onClick={onAlertClick}
      cursor="pointer"
      showProgressBar={!isLoading}
      showProgressPercent={!isLoading}
      progressSlot={
        isLoading ? (
          <LinearLoaderWithTime
            isAlert={true}
            labelSide="bottom"
            time={loaderTimeMs}
            progress={seedProgress}
            autoIncrement={true}
            max={99}
            onProgressChange={onProgressChange}
          />
        ) : null
      }
      details={
        message ? (
          isError ? (
            <TaskAlertErrorMessage>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ fontSize: "0.75rem" }}
              >
                {message}
              </Typography>
            </TaskAlertErrorMessage>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {message}
            </Typography>
          )
        ) : null
      }
      actions={
        showDiscardButton ? (
          <TaskAlertDiscardActions
            isConfirming={isConfirmingDiscard}
            descriptionKey={DISCARD_IMAGE_ANIMATION_ALERT_DESCRIPTION}
            onStartDiscard={() => {
              if (isSuccess) {
                setIsConfirmingDiscard(true);
                return;
              }
              onClose(id);
            }}
            onConfirm={() => onClose(id)}
            onCancel={() => setIsConfirmingDiscard(false)}
          />
        ) : null
      }
    />
  );
});

ImageAnimationAlertItem.displayName = "ImageAnimationAlertItem";

export default ImageAnimationAlertItem;
